import { useState, useRef, useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ContentRow as ContentRowType } from '@/types';
import Badge from '@/components/ui/Badge/Badge';
import styles from './ContentRow.module.css';

interface ContentRowProps {
  row: ContentRowType;
  children: ReactNode;
  defaultCollapsed?: boolean;
  loading?: boolean;
  onDelete?: () => void;
}

const SKELETON_COUNT = 6;

function SkeletonShelf() {
  return (
    <div className={styles.shelf} aria-busy="true" aria-label="Loading content">
      {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
        <div key={i} className={styles.skeletonTile}>
          <div className={styles.skeletonThumb} />
          <div className={styles.skeletonBody}>
            <div className={`${styles.skeletonLine} ${styles.skeletonLineWide}`} />
            <div className={`${styles.skeletonLine} ${styles.skeletonLineNarrow}`} />
            <div className={`${styles.skeletonLine} ${styles.skeletonLineXnarro}`} />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyShelf({ type }: { type: ContentRowType['type'] }) {
  const isPodcast = type === 'pod';
  return (
    <div className={styles.emptyShelf} role="status" aria-label="No content">
      {isPodcast ? (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
          <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="22" />
        </svg>
      ) : (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </svg>
      )}
      <p className={styles.emptyTitle}>No content yet</p>
      <p className={styles.emptySub}>{isPodcast ? 'Podcast episodes will appear here once ingested.' : 'Check back soon — new content is indexed daily.'}</p>
    </div>
  );
}

export default function ContentRow({ row, children, defaultCollapsed = false, loading = false, onDelete: _onDelete }: ContentRowProps) {
  const navigate = useNavigate();
  // Collapse when explicitly told to, or when the section has no items
  const [collapsed, setCollapsed] = useState(() => defaultCollapsed || (!loading && row.items.length === 0));
  const hadItemsRef = useRef(row.items.length > 0);
  const prevLoadingRef = useRef(loading);
  const prevDefaultCollapsed = useRef(defaultCollapsed);

  // Sync collapsed state when the defaultCollapsed prop changes (e.g. chip filter toggle)
  useEffect(() => {
    if (prevDefaultCollapsed.current !== defaultCollapsed) {
      prevDefaultCollapsed.current = defaultCollapsed;
      if (row.items.length > 0) {
        setCollapsed(defaultCollapsed);
      }
    }
  }, [defaultCollapsed, row.items.length]);

  // Auto-expand when content loads in to a previously empty section;
  // auto-collapse when loading finishes and section has no items.
  useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    prevLoadingRef.current = loading;

    if (!hadItemsRef.current && row.items.length > 0) {
      // Items just arrived — expand
      setCollapsed(false);
    } else if (wasLoading && !loading && row.items.length === 0) {
      // Loading finished with no items — collapse
      setCollapsed(true);
    }
    hadItemsRef.current = row.items.length > 0;
  }, [loading, row.items.length]);

  return (
    <div className={styles.row}>
      <div className={styles.head}>
        <div className={styles.headLeft}>
          <div className={styles.titleRow}>
            <button
              className={styles.collapseBtn}
              onClick={() => setCollapsed((c) => !c)}
              aria-expanded={!collapsed}
              aria-label={collapsed ? 'Expand section' : 'Collapse section'}
            >
              <svg
                className={`${styles.chevron} ${collapsed ? styles.chevronCollapsed : ''}`}
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            <span className={styles.title} dangerouslySetInnerHTML={{ __html: row.title }} />
            {row.subtitle && (
              <span className={styles.subtitle}>· {row.subtitle}</span>
            )}
            {row.badge && (
              <Badge variant={row.badgeVariant ?? 'green'}>{row.badge}</Badge>
            )}
          </div>
        </div>
        <div className={styles.headRight}>
          {row.items.length > 0 && (
            <button
              className={styles.viewAll}
              onClick={() => navigate(`/section/${row.id}`)}
              aria-label={`View all ${row.title}`}
            >
              See all →
            </button>
          )}
        </div>
      </div>
      {!collapsed && (
        loading
          ? <SkeletonShelf />
          : row.items.length === 0
            ? <EmptyShelf type={row.type} />
            : <div className={styles.shelf}>{children}</div>
      )}
    </div>
  );
}
